// 引入 sanitize-html，用于剥离危险标签防 XSS。
import sanitizeHtml from 'sanitize-html';
// 复用平台已有的"受支持第三方域名"白名单（视频嵌入域：bilibili/youku/qq/skypixel/djivideos...）。
import { isValidDomain } from './url';
// 复用平台已有的图片域名清单（constants/url.ts#supportWebpDomain，含 test/prod 各环境），避免写死某个环境的桶。
import { supportWebpDomain } from '@/constants/url';

// 帖子正文允许保留的标签：在默认白名单基础上补充正文会用到的图片/内嵌视频。
const POST_ALLOWED_TAGS = [
  ...sanitizeHtml.defaults.allowedTags,
  'img',
  'iframe',
  'figure',
  'figcaption',
];

// 帖子正文允许保留的属性。`*` 兜底仅放行 class / style / data-type（inline-link 点击逻辑依赖 data-type）。
const POST_ALLOWED_ATTRIBUTES: sanitizeHtml.IOptions['allowedAttributes'] = {
  '*': ['class', 'style', 'data-type'],
  a: ['href', 'target', 'rel'],
  img: ['src', 'alt', 'width', 'height'],
  iframe: ['src', 'width', 'height', 'frameborder', 'allowfullscreen', 'allow', 'title'],
};

// style 属性**不做属性级白名单**（避免误伤正常排版），只去掉其中能"外连资源/执行"的构造：
// url(...) / expression(...) / javascript: / @import / behavior / -moz-binding。
// 目的：防止用 `style="background:url(//attacker/x)"` 之类外连做 beacon。
const STYLE_EXFIL_RE = /url\s*\(|expression\s*\(|javascript:|@import|behavior\s*:|-moz-binding/i;
const stripDangerousStyle = (attribs: Record<string, string>) => {
  if (attribs.style && STYLE_EXFIL_RE.test(attribs.style)) {
    delete attribs.style;
  }
  return attribs;
};

// 允许加载图片的平台域名：复用 constants/url.ts#supportWebpDomain（含 test/prod 各环境，CommonImage 也用它）。
// 单一来源，避免另起一份图片域清单导致漂移。目的：阻止正文里用外站图片做 beacon，泄露访客 IP / UA / Referer。
const POST_IMG_HOSTS = new Set(supportWebpDomain);

const isTrustedImgUrl = (src: string | undefined): boolean => {
  if (!src) return false;
  // 允许编辑器内联的 base64 图片。
  if (/^data:image\//i.test(src)) return true;
  // 允许同源相对路径（`/a.png`），但排除协议相对 `//evil.com`。
  if (src.startsWith('/') && !src.startsWith('//')) return true;
  try {
    const url = new URL(src);
    if (url.protocol !== 'https:') return false;
    return POST_IMG_HOSTS.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
};

/**
 * 净化帖子正文 HTML（`special === 6` 内容数组里的文本项 `desc`）。
 *
 * 该内容是服务端存下的用户 HTML，会经 `dangerouslySetInnerHTML` 渲染，
 * 必须在**渲染前**剥离 `<script>` / 事件属性（onerror 等）/ 危险 URL，
 * 否则构成存储型 XSS。
 *
 * 采用 `sanitize-html`（可在 Node/SSR 运行）而非 `createMarkup` 的 DOMPurify
 * —— DOMPurify 在 SSR 下未初始化会抛错，而正文会在服务端渲染。
 */
export const sanitizePostHtml = (html = ''): string => {
  if (!html) return '';
  return sanitizeHtml(html, {
    allowedTags: POST_ALLOWED_TAGS,
    allowedAttributes: POST_ALLOWED_ATTRIBUTES,
    // 链接协议白名单；img 额外允许 data:（编辑器可能内联 base64 图片）。
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    allowedSchemesByTag: { img: ['https', 'data'] },
    // style 保留原样，仅剥离其中能外连/执行的构造（见 stripDangerousStyle）。
    transformTags: {
      '*': (tagName, attribs) => ({ tagName, attribs: stripDangerousStyle(attribs) }),
    },
    // 内嵌视频限平台**受支持**的第三方视频域（含子域），见 url.ts#isValidDomain。
    // 这里不用 sanitize-html 的 allowedIframeHostnames（只支持精确域名），改在 exclusiveFilter 用 isValidDomain 做后缀匹配。
    // img 同理需要限制域名（防外站 beacon）。统一收口：
    //   - iframe：src 缺失或不在受支持视频域 → 丢弃
    //   - img：src 不在平台图片白名单 → 丢弃
    exclusiveFilter: (frame) => {
      if (frame.tag === 'iframe') return !frame.attribs.src || !isValidDomain(frame.attribs.src);
      if (frame.tag === 'img') return !isTrustedImgUrl(frame.attribs.src);
      return false;
    },
  });
};

// 定义 HTML 实体解码函数的可选参数类型。
export interface DecodeHtmlEntitiesOptions {
  // 是否优先使用 DOM 能力进行解码（浏览器环境更完整）。
  preferDom?: boolean;
  // 额外的命名实体映射，用于补充默认映射之外的实体。
  namedEntityMap?: Record<string, string>;
}

// 高亮链接的内联样式常量。
const LINK_STYLE = 'color: #0060EF;';

// 根据是否新开页签，构造链接的完整属性字符串。
const getLinkAttrs = (openInNewTab: boolean) =>
  openInNewTab
    ? `target="_blank" rel="noopener noreferrer" style="${LINK_STYLE}"`
    : `style="${LINK_STYLE}"`;

/**
 * 将**纯文本**中的 http/https URL 转换为高亮可点击链接。
 *
 * 后端约定：URL 后必须用空格与其他文本隔开，否则后续内容视为 URL 的一部分。
 * 正则 `/https?:\/\/[^\s<"']+/gi` 在遇到空格、尖括号或引号时停止，
 * 防止引号被吞入 URL 后闭合 href 属性导致 XSS 注入。
 *
 * 输入应为已去除 HTML 标签的纯文本。函数先用 sanitize-html 剥离所有标签
 * （`allowedTags: []`，等价于 HTML 转义，防 XSS），再匹配 URL 并包裹 `<a>` 标签。
 *
 * @param openInNewTab 是否在新页签打开（PC 默认 true，移动端传 false 原地跳转）
 */
export const linkifyUrls = (text = '', openInNewTab = true): string => {
  if (!text) return '';
  // 用 sanitize-html 剥离所有标签，防止 XSS 注入。
  const safe = sanitizeHtml(text, { allowedTags: [] });
  const linkAttrs = getLinkAttrs(openInNewTab);
  // 匹配 http:// 或 https:// 开头的 URL，遇到空格、尖括号或引号停止（防止引号闭合 href 属性）。
  return safe.replace(
    /(https?:\/\/[^\s<"']+)/gi,
    (url) => `<a href="${url}" ${linkAttrs}>${url}</a>`
  );
};

/**
 * 处理 **HTML 内容** 中的 URL：
 * 1. 用 sanitize-html 净化 HTML，剥离危险标签和属性（防 XSS）。
 * 2. 为已有的 `<a>` 标签补充高亮颜色；当 openInNewTab 为 true 时补充 `target="_blank"`。
 * 3. 将纯文本中的 URL（不在任何标签内）转换为高亮 `<a>` 标签。
 *
 * 后端约定：URL 后必须用空格与其他文本隔开，否则后续内容视为 URL 的一部分。
 * 使用占位符技术先将已有 `<a>` 标签摘出，避免对标签内 URL 重复匹配。
 *
 * @param openInNewTab 是否在新页签打开（PC 默认 true，移动端传 false 原地跳转）
 */
export const linkifyHtmlUrls = (html = '', openInNewTab = true): string => {
  if (!html) return '';
  const placeholders: string[] = [];
  const linkAttrs = getLinkAttrs(openInNewTab);

  // 0. 用 sanitize-html 净化，保留 <a> 标签，剥离其他危险标签和属性。
  let result = sanitizeHtml(html, {
    allowedTags: ['a', 'b', 'i', 'em', 'strong', 'br'],
    allowedAttributes: { a: ['href', 'target', 'rel', 'style'] },
  });

  // 1. 提取已有 <a> 标签，补充 target / style 后替换为占位符。
  result = result.replace(/<a\s[^>]*>[\s\S]*?<\/a>/gi, (match) => {
    const processed = match.replace(/<a\s+([^>]*?)>/i, (_m, attrs: string) => {
      let newAttrs = attrs;
      if (openInNewTab) {
        // 新开页签：补 target / rel（已有则不重复添加）。
        if (!/target=/i.test(newAttrs)) {
          newAttrs += ` target="_blank" rel="noopener noreferrer"`;
        }
      } else {
        // 原地跳转：剥离已有的 target="_blank" 和关联的 rel 属性。
        newAttrs = newAttrs
          .replace(/\s*target=(['"]).*?\1/gi, '')
          .replace(/\s*rel=(['"]).*?\1/gi, '');
      }
      if (!/style=/i.test(newAttrs)) {
        newAttrs += ` style="${LINK_STYLE}"`;
      }
      return `<a ${newAttrs}>`;
    });
    const placeholder = `__LINK_PLACEHOLDER_${placeholders.length}__`;
    placeholders.push(processed);
    return placeholder;
  });

  // 2. 将纯文本中的 URL 转换为 <a> 标签（遇到空格、尖括号或引号停止）。
  result = result.replace(
    /(https?:\/\/[^\s<"']+)/gi,
    (url) => `<a href="${url}" ${linkAttrs}>${url}</a>`
  );

  // 3. 恢复占位符。
  result = result.replace(
    /__LINK_PLACEHOLDER_(\d+)__/g,
    (_m, i: string) => placeholders[Number(i)] ?? ''
  );

  return result;
};

// 导出统一的 HTML 实体解码函数。
export const decodeHtmlEntities = (
  // 待解码文本，默认空字符串。
  text = '',
  // 解码选项，默认空对象。
  options: DecodeHtmlEntitiesOptions = {}
) => {
  // 输入为空时直接返回空字符串。
  if (!text) return '';
  // 解析选项并提供默认值。
  const { preferDom = true, namedEntityMap = {} } = options;

  // 在允许且存在 document 的环境中优先走 DOM 解码。
  if (preferDom && typeof document !== 'undefined') {
    // 创建临时容器用于浏览器原生实体解析。
    let temp: HTMLDivElement | null = document.createElement('div');
    // 将原始文本写入 innerHTML 触发实体解析。
    temp.innerHTML = text;
    // 读取解码后的纯文本内容。
    let decoded = temp.textContent || temp.innerText || '';
    // 释放临时对象引用。
    temp = null;
    // 应用额外命名实体映射（处理 DOM 未覆盖或业务自定义实体）。
    Object.entries(namedEntityMap).forEach(([entity, value]) => {
      // 将形如 &entity; 的文本替换成目标字符。
      decoded = decoded.replace(new RegExp(`&${entity};`, 'g'), value);
    });
    // 返回 DOM 分支解码结果。
    return decoded;
  }

  // （兜底方案）在非浏览器或不使用 DOM 时走正则回退解码。
  let decoded = text
    // 还原不换行空格实体。
    .replace(/&nbsp;/g, ' ')
    // 还原与号实体。
    .replace(/&amp;/g, '&')
    // 还原双引号实体。
    .replace(/&quot;/g, '"')
    // 还原单引号实体。
    .replace(/&#39;/g, "'")
    // 还原小于号实体。
    .replace(/&lt;/g, '<')
    // 还原大于号实体。
    .replace(/&gt;/g, '>')
    // 还原十进制数字实体。
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(Number(dec)))
    // 还原十六进制数字实体。
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
      String.fromCodePoint(parseInt(hex, 16))
    );

  // 应用额外命名实体映射（回退分支）。
  Object.entries(namedEntityMap).forEach(([entity, value]) => {
    // 将形如 &entity; 的文本替换成目标字符。
    decoded = decoded.replace(new RegExp(`&${entity};`, 'g'), value);
  });
  // 返回最终解码结果。
  return decoded;
};
